import {useTranslation} from 'react-i18next';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {AccordionSummary, Box, Grid, Typography} from '@mui/material';

import {AssignedCompanyAdmins} from '../forms/JobPostingForm/AssignedCompanyAdmins';

import {empionGreen, empionOrange} from '../../../../../../layouts/theme/EmpionColors';
import {transformDate} from '../../../../../../utils/dateUtils/dateFormattingUtils';
import {JobPosting} from '../../types/JobPostingsTypes';

interface JobPostingsAccordionSummaryProps {
  jobPosting: JobPosting;
  renderJobPostingLocations: (jobPosting: JobPosting) => any;
  expanded: string | false | undefined;
  theme?: any;
  renderIconStatus(jobPosting: JobPosting): any;
}

export default function JobPostingsAccordionSummary({
  jobPosting,
  renderJobPostingLocations,
  expanded,
  theme,
  renderIconStatus,
}: JobPostingsAccordionSummaryProps) {
  const {t} = useTranslation();

  const applyConditionalStylesOnAccordionSummary = () => {
    if (expanded === jobPosting.resourceId) {
      return {
        backgroundColor: theme.palette.mode === 'dark' ? empionGreen[900] : 'white',
        padding: '0px 30px 0px 30px',
        transition: 'all 0.2s ease-in-out',
        borderBottom: theme.palette.mode === 'dark' ? `1px solid ${empionGreen[600]}` : `1px solid ${empionOrange[3]}`,
      };
    }
  };

  const accordionSummaryStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    '&:hover ': {
      backgroundColor: theme.palette.mode === 'dark' ? empionGreen[900] : 'white',
      padding: '0px 30px 0px 30px',
      transition: 'all 0.2s ease-in-out',
    },
    ...applyConditionalStylesOnAccordionSummary(),
  };

  const body1Style = {
    color: theme.palette.mode === 'dark' ? 'white' : empionGreen[700],
  };

  return (
    <AccordionSummary
      aria-controls={`job-posting-summary-${jobPosting.resourceId}`}
      id={`job-posting-header-${jobPosting.resourceId}`}
      sx={accordionSummaryStyles}
    >
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: {
            xs: '300px',
            sm: '700px',
            md: '100%',
          },
        }}
      >
        <Grid item xs={12} sm={3} md={4}>
          <Typography
            variant='h4'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {jobPosting.title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant='body1'
              sx={{
                color: theme.palette.mode === 'dark' ? empionGreen[40] : empionGreen[100],
              }}
            >
              {renderJobPostingLocations(jobPosting)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Typography variant='body1' sx={body1Style}>
            {jobPosting.publishedAt
              ? transformDate(jobPosting?.publishedAt || '', 'numeric')
              : transformDate(jobPosting?.updatedAt || '', 'numeric')}
            {renderIconStatus(jobPosting)}
          </Typography>
        </Grid>

        <Grid
          item
          xs={6}
          sm={3}
          md={2}
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <Typography variant='body1' sx={body1Style}>
            {t('shared.roleTypes.applicants')}
          </Typography>
          <Typography variant='h4'>{jobPosting.matchesCount?.toLocaleString()}</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={2}
          md={2}
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <AssignedCompanyAdmins companyAdmins={jobPosting?.companyAdmins || []} />
        </Grid>
        <Grid
          item
          xs={12}
          sm={1}
          md={2}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingBottom: '25px',
          }}
        >
          <KeyboardArrowDownIcon
            fontSize='large'
            sx={{
              color: theme.palette.mode === 'dark' ? empionGreen[40] : empionGreen[100],
              transform: expanded === jobPosting.resourceId ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Grid>
      </Grid>
    </AccordionSummary>
  );
}
